"use client";

import {
  detectNativeUpiApps,
  type NativeUpiAppDetection,
} from "@/lib/native/payment-navigation";
import { listSupportedUpiIntentApps } from "@/lib/razorpay-custom-checkout";

/**
 * The UPI apps we can offer as one-tap tiles, and how we learn which of them
 * this device actually has.
 *
 * ## Why tiles at all, when we cannot detect installs
 *
 * Because detection was never what made Razorpay's screen bad. On Android,
 * tapping "PhonePe" can hand the request to the OS intent resolver directly.
 * On iOS we do not render these tiles at all: Safari can neither verify an app
 * nor let JavaScript catch its native "address is invalid" page.
 *
 * A failed Android handoff returns to the method sheet without changing this
 * list. Razorpay reports the same `intent_no_apps_error` when the payer cancels
 * an app-opening prompt, so learning availability from that response would
 * hide an app the customer demonstrably uses.
 *
 * ## Where the list comes from
 *
 * `getSupportedUpiIntentApps()` is a platform filter and nothing more: it
 * resolves a static array chosen from the user agent and never inspects the
 * device. It says which app shortcodes Razorpay can address on Android, not
 * what is installed. The UI and recovery copy preserve that distinction.
 *
 * ## Except inside the native shell
 *
 * The Capacitor shell CAN answer the question a browser cannot: iOS
 * `canOpenURL` and Android's `upi://pay` resolver both know what is installed.
 * So a binary that has `HiivePaymentNavigation.listUpiApps` gets tiles that are
 * the intersection of Razorpay's list (built with `webview_intent`, without
 * which the SDK lists nothing in a WebView) and the apps the OS confirmed.
 * There — and only there — the rows may be described as installed. A binary
 * without the method keeps the provider-managed row it always had.
 */
export type UpiAppOption = {
  /** The identifier `createPayment(data, { app })` expects. */
  shortcode: string;
  name: string;
  iconUrl: string | null;
};

/**
 * Names and icons for the shortcodes `getSupportedUpiIntentApps()` can return.
 *
 * Keyed by the shortcode as that method emits it — note it rewrites
 * `google_pay` to `gpay` on the way out, and `gpay` is what `createPayment`
 * accepts, so `gpay` is the key here.
 *
 * The marks are served from our own `public/upi-apps/`, downsized to 56px (2x
 * of the 28px they render at) and totalling ~52 KB for all thirteen. They were
 * hotlinked from `cdn.razorpay.com` first, which was wrong twice over: it put a
 * third-party CDN in the critical path of a payment screen, and two of the URLs
 * answer 403 for everyone, so those tiles fired a request that could only fail.
 *
 * `iconUrl: null` means we could not source a mark at all — the tile renders
 * the app's initial, which is why nothing here ever needs a placeholder file.
 */
const UPI_APP_CATALOGUE: Record<
  string,
  { name: string; iconUrl: string | null }
> = {
  gpay: { name: "Google Pay", iconUrl: "/upi-apps/gpay.svg" },
  phonepe: { name: "PhonePe", iconUrl: "/upi-apps/phonepe.svg" },
  paytm: { name: "Paytm", iconUrl: "/upi-apps/paytm.svg" },
  cred: { name: "CRED", iconUrl: "/upi-apps/cred.png" },
  bhim: { name: "BHIM", iconUrl: "/upi-apps/bhim.svg" },
  amazon: { name: "Amazon Pay", iconUrl: "/upi-apps/amazon.svg" },
  mobikwik: { name: "MobiKwik", iconUrl: "/upi-apps/mobikwik.png" },
  super_money: { name: "Super.Money", iconUrl: "/upi-apps/super_money.png" },
  popclubapp: { name: "POP UPI", iconUrl: "/upi-apps/popclubapp.svg" },
  navi: { name: "Navi", iconUrl: "/upi-apps/navi.png" },
  jupiter: { name: "Jupiter", iconUrl: "/upi-apps/jupiter.png" },
  slice: { name: "Slice", iconUrl: "/upi-apps/slice.png" },
  payzapp: { name: "PayZapp", iconUrl: "/upi-apps/payzapp.png" },
  /**
   * No mark available: Razorpay's own `moneyview.png` and `imobile-icici.png`
   * answer 403 to everyone (checked 2026-09-03) and there is no other first-
   * party source, so these two render as initials.
   */
  moneyview: { name: "MoneyView", iconUrl: null },
  icici: { name: "iMobile by ICICI", iconUrl: null },
};

/**
 * Razorpay's own ordering of the apps that carry most UPI volume. Kept because
 * a payer scanning for their app finds it fastest where they expect it, and the
 * long tail below is alphabetical noise to everyone but its own users.
 */
const PREFERRED_ORDER = ["gpay", "phonepe", "paytm", "cred", "bhim"];

/**
 * How many tiles show before "View all".
 *
 * Fifteen rows is a scroll, and a scroll on a payment screen is friction of the
 * kind this whole flow exists to remove. Four covers the overwhelming majority
 * of UPI volume, matches the shape the provider's own screen uses, and leaves
 * the rest one tap away rather than hidden.
 */
export const UPI_APP_PREVIEW_COUNT = 4;

/**
 * The `app` value that means "let the OS decide".
 *
 * Resolves to the SDK's `{shortcode:"all", url_schema:"upi://pay"}` — a bare
 * UPI intent. Android answers it with its own chooser, built from what is
 * genuinely installed, which is the honest catch-all under the tiles. iOS has
 * no generic `upi://` handler, so it never offers this.
 */
export const UPI_ANY_APP = "any";

const LEGACY_UNAVAILABLE_STORAGE_KEY = "hiive.upi.unavailable_apps.v1";

/**
 * Remove the unsafe availability guesses written by the previous release.
 *
 * Called from the controller on every surface, not just where a list is
 * loaded: the release that wrote this key hid apps on iOS too, so an iPhone
 * that never asks for tiles again would otherwise keep a month-old wrong guess
 * in storage forever. Nothing reads it any more, but leaving it there means the
 * next person to grep for it has to prove that.
 */
export const clearLegacyUnavailableUpiApps = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(LEGACY_UNAVAILABLE_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in private mode. The key is ignored anyway,
    // so a failed cleanup cannot affect which methods are shown.
  }
};

const toOption = (shortcode: string): UpiAppOption | null => {
  const entry = UPI_APP_CATALOGUE[shortcode];
  if (!entry) return null;
  return { shortcode, name: entry.name, iconUrl: entry.iconUrl ?? null };
};

export const orderUpiApps = (shortcodes: string[]): UpiAppOption[] => {
  const unique = Array.from(new Set(shortcodes)).filter(
    // `any` is the catch-all row, not a tile, and it is rendered separately.
    (shortcode) => shortcode !== UPI_ANY_APP,
  );

  const rank = (shortcode: string) => {
    const index = PREFERRED_ORDER.indexOf(shortcode);
    return index === -1 ? PREFERRED_ORDER.length : index;
  };

  return unique
    .map(toOption)
    .filter((option): option is UpiAppOption => option !== null)
    .sort((left, right) => rank(left.shortcode) - rank(right.shortcode));
};

export type UpiAppAvailability = {
  /** App tiles supported by this version of Razorpay. */
  apps: UpiAppOption[];
  /** Whether a bare `upi://` catch-all row is worth offering (Android). */
  supportsAnyApp: boolean;
  /**
   * Where the list came from, and so what it may claim.
   *
   * - `provider_platform_list` — Razorpay's static user-agent list (mobile
   *   web). Says what Razorpay can address, never what is installed.
   * - `native_installed` — intersected with what the native shell's OS
   *   confirmed is installed. The only source whose rows may say "installed".
   */
  source: "provider_platform_list" | "native_installed";
};

/**
 * The tile list for this device.
 *
 * Returns empty on desktop without any platform check of our own — the SDK's
 * own list is already `[]` there, because there is no app to hand a payment to.
 */
export const loadUpiAppAvailability = async (
  razorpayKeyId: string,
): Promise<UpiAppAvailability> => {
  const supported = await listSupportedUpiIntentApps(razorpayKeyId);

  return {
    apps: orderUpiApps(supported),
    supportsAnyApp: supported.includes(UPI_ANY_APP),
    source: "provider_platform_list",
  };
};

/**
 * Tiles for the native shell: what Razorpay can address AND the OS confirmed.
 *
 * Pure so the rule can be pinned in a test without a bridge or an SDK:
 *
 * - A tile needs both halves. Razorpay's list alone is the web's guess; the
 *   OS's alone would offer a shortcode `createPayment` refuses locally.
 * - Ordering is `orderUpiApps`' — the same PREFERRED_ORDER the web uses.
 * - "Other UPI app" (`any`, a bare `upi://pay`) needs the SDK to offer it
 *   (Android only; iOS has no generic handler) AND the OS to report at least
 *   one UPI handler, since an empty chooser is a dead end.
 *
 * An empty result is a real answer — "nothing installed" — and the sheet then
 * shows its provider-managed row, QR, UPI ID and cards, exactly as before.
 */
export const intersectInstalledUpiApps = (
  supported: string[],
  installed: { apps: string[]; hasAnyUpiApp: boolean },
): UpiAppAvailability => {
  const installedSet = new Set(installed.apps);
  return {
    apps: orderUpiApps(
      supported.filter((shortcode) => installedSet.has(shortcode)),
    ),
    supportsAnyApp: supported.includes(UPI_ANY_APP) && installed.hasAnyUpiApp,
    source: "native_installed",
  };
};

/**
 * The tile list inside the native shell, or `null` when the shell could not
 * say what is installed.
 *
 * `null` is the old-binary answer and it means "change nothing": no tiles, the
 * single provider-managed row, `webview_intent` Standard Checkout — the path
 * every build before `listUpiApps` has always had. The native probe runs first
 * so an old binary never waits on the SDK for a list it will not use.
 */
export const loadNativeUpiAppAvailability = async (
  razorpayKeyId: string,
  detect: () => Promise<NativeUpiAppDetection> = detectNativeUpiApps,
  listSupported: typeof listSupportedUpiIntentApps = listSupportedUpiIntentApps,
): Promise<UpiAppAvailability | null> => {
  const detection = await detect();
  if (detection.status !== "detected") return null;

  const supported = await listSupported(razorpayKeyId, {
    webviewIntent: true,
  });
  return intersectInstalledUpiApps(supported, detection);
};
